/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';
import { ErrorBoundary } from 'react-error-boundary';

import { type SuggestionItemResponse } from '@atlassian/navigation-bff-data-provider';

import { LOOM_ATI } from '../../../constants';
import type { SourceProduct, SuggestionConfigContext } from '../../../types';

import { LinkItem } from './primitives/LinkItem/LinkItem';
import { LoomVideoItem } from './primitives/LoomItem/LoomVideoItem';
import { SuggestionItemBase } from './SuggestionItemBase';

export interface SuggestionItemProps {
	/**
	 * Additional context for the component
	 */
	context?: SuggestionConfigContext;

	/**
	 * The index of this item within the suggestion section (0-based)
	 */
	itemIndex: number;

	/**
	 * The source product context
	 */
	product: SourceProduct;

	/**
	 * The response item from Universal Create
	 */
	responseItem: SuggestionItemResponse;

	/**
	 * The total count of items in the suggestion section
	 */
	totalItemsCount: number;
}

function SuggestionItemInner({
	responseItem,
	context,
	product,
	itemIndex,
	totalItemsCount,
}: SuggestionItemProps) {
	if (context) {
		switch (responseItem.type) {
			case 'platform':
				if (responseItem.ati === LOOM_ATI) {
					return (
						<LoomVideoItem
							responseItem={responseItem}
							context={context}
							product={product}
							itemIndex={itemIndex}
							totalItemsCount={totalItemsCount}
						/>
					);
				}
				break;
			case 'link':
				return (
					<LinkItem
						responseItem={responseItem}
						context={context}
						product={product}
						itemIndex={itemIndex}
						totalItemsCount={totalItemsCount}
					/>
				);
			default:
				break;
		}
	}

	// Return GenericMenuItem with no customisedItemConfig for non-Loom ATIs
	return (
		<SuggestionItemBase
			responseItem={responseItem}
			itemIndex={itemIndex}
			totalItemsCount={totalItemsCount}
		/>
	);
}

/**
 * A component that renders a suggestion item.
 * Wrapped by the ErrorBoundary component to handle errors so that it won't break the whole menu.
 *
 * @param props - @see SuggestionItemProps
 * @returns - JSX.Element @see SuggestionItemInner
 */
export function SuggestionItem(props: SuggestionItemProps) {
	return (
		<ErrorBoundary fallback={null}>
			<SuggestionItemInner {...props} />
		</ErrorBoundary>
	);
}
